import React, { useState, useEffect } from 'react';
// UI
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  ThemeProvider,
  createTheme,
  CssBaseline,
  TextField,
  FormControlLabel,
  Checkbox,
  Link,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import AppleIcon from '@mui/icons-material/Apple';
// Context
import { useAuth } from 'AuthContext';
// Utils
import { useSearchParams, useNavigate } from 'react-router-dom';
import { baseThemeOptions } from 'theme';
import {
  PRIVACY_POLICY_URL,
  TERMS_OF_USE_URL,
  buildTermsAcceptanceSettings,
} from 'utils/legalLinks';
// Services
import { updateUserProfile } from 'services/users/users.service';

const theme = createTheme({
  palette: {
    primary: {
      main: '#5D473A',
    },
    secondary: {
      main: '#BFA480',
    },
    background: {
      default: '#F5F1EA',
    },
  },
  typography: baseThemeOptions.typography,
});

const SignIn = () => {
  const { signInWithGoogle, signInWithApple, signInWithEmail, signUpWithEmail, user, setUserProfile } =
    useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [inviteCode, setInviteCode] = useState('');
  const [useEmailAuth, setUseEmailAuth] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    const codeFromUrl = searchParams.get('inviteCode');
    if (codeFromUrl) {
      setInviteCode(codeFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    if (user) {
      if (inviteCode) {
        navigate(`/join-club?inviteCode=${encodeURIComponent(inviteCode)}`);
      } else {
        navigate('/');
      }
    }
  }, [user, inviteCode, navigate]);

  const persistTermsAcceptance = async (firebaseUser) => {
    if (!firebaseUser?.uid) return;
    try {
      const updated = await updateUserProfile(firebaseUser.uid, {
        settings: buildTermsAcceptanceSettings(),
      });
      if (setUserProfile) {
        setUserProfile(updated);
      }
    } catch (err) {
      console.error('Failed to persist terms acceptance:', err);
    }
  };

  const ensureTermsAgreed = () => {
    if (!agreedToTerms) {
      setError('Please agree to the Terms of Use and Privacy Policy to continue.');
      return false;
    }
    return true;
  };

  const handleGoogleSignIn = async () => {
    if (!ensureTermsAgreed()) return;
    try {
      setLoading(true);
      setError(null);
      const firebaseUser = await signInWithGoogle();
      await persistTermsAcceptance(firebaseUser);
    } catch (signInError) {
      setError(signInError?.message || 'Failed to sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    if (!ensureTermsAgreed()) return;
    try {
      setLoading(true);
      setError(null);
      const firebaseUser = await signInWithApple();
      await persistTermsAcceptance(firebaseUser);
    } catch (signInError) {
      setError(signInError?.message || 'Failed to sign in with Apple. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (event) => {
    event.preventDefault();

    if (!ensureTermsAgreed()) return;

    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }

    if (isSignUp && !displayName.trim()) {
      setError('Please enter your name.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let authResult;
      if (isSignUp) {
        authResult = await signUpWithEmail(email.trim(), password, displayName.trim());
      } else {
        authResult = await signInWithEmail(email.trim(), password);
      }
      await persistTermsAcceptance(authResult?.user || authResult);
    } catch (signInError) {
      setError('Failed to sign in. Please check your details and try again.');
    } finally {
      setLoading(false);
    }
  };

  const termsCheckbox = (
    <FormControlLabel
      control={
        <Checkbox
          checked={agreedToTerms}
          onChange={(event) => setAgreedToTerms(event.target.checked)}
          color="primary"
          disabled={loading}
        />
      }
      sx={{ alignItems: 'flex-start', textAlign: 'left', mx: 0 }}
      label={
        <Typography variant="body2" color="text.secondary">
          I agree to the{' '}
          <Link href={TERMS_OF_USE_URL} target="_blank" rel="noopener noreferrer">
            Terms of Use
          </Link>{' '}
          and{' '}
          <Link href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </Link>
          . CBC has zero tolerance for objectionable content and abusive users.
        </Typography>
      }
    />
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #F5F1EA 0%, #BFA480 100%)',
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 400, width: '100%' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h4" component="h1" gutterBottom color="primary">
              Conscious Book Club
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Sign in to access your reading goals and connect with fellow book lovers.
              New users can create an account in seconds.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {inviteCode && (
              <Alert severity="info" sx={{ mb: 2 }}>
                You'll join the club with invite code: <strong>{inviteCode}</strong>
              </Alert>
            )}

            <Stack spacing={2}>
              {termsCheckbox}

              <Button
                variant="outlined"
                onClick={() => setUseEmailAuth((prev) => !prev)}
                disabled={loading}
                sx={{ textTransform: 'none' }}
              >
                {useEmailAuth ? 'Use social sign-in instead' : 'Use email and password'}
              </Button>

              {useEmailAuth ? (
                <Box component="form" onSubmit={handleEmailSubmit}>
                  <Stack spacing={2}>
                    {isSignUp && (
                      <TextField
                        label="Name"
                        value={displayName}
                        onChange={(event) => setDisplayName(event.target.value)}
                        fullWidth
                      />
                    )}
                    <TextField
                      label="Email"
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Password"
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      fullWidth
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      disabled={loading || !agreedToTerms}
                      startIcon={loading ? <CircularProgress size={20} /> : null}
                      sx={{
                        py: 1.5,
                        fontSize: '1.05rem',
                        textTransform: 'none',
                        borderRadius: 2,
                      }}
                    >
                      {loading
                        ? 'Signing in...'
                        : isSignUp
                          ? 'Create account'
                          : 'Sign in with email'}
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => setIsSignUp((prev) => !prev)}
                      disabled={loading}
                      sx={{ textTransform: 'none' }}
                    >
                      {isSignUp
                        ? 'Already have an account? Sign in'
                        : 'Need an account? Sign up'}
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handleGoogleSignIn}
                    disabled={loading || !agreedToTerms}
                    startIcon={loading ? <CircularProgress size={20} /> : <GoogleIcon />}
                    sx={{
                      py: 1.5,
                      fontSize: '1.1rem',
                      textTransform: 'none',
                      borderRadius: 2,
                    }}
                  >
                    {loading ? 'Signing in...' : 'Continue with Google'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    onClick={handleAppleSignIn}
                    disabled={loading || !agreedToTerms}
                    startIcon={loading ? <CircularProgress size={20} /> : <AppleIcon />}
                    sx={{
                      py: 1.5,
                      fontSize: '1.1rem',
                      textTransform: 'none',
                      borderRadius: 2,
                    }}
                  >
                    {loading ? 'Signing in...' : 'Continue with Apple'}
                  </Button>
                </Stack>
              )}

              {inviteCode && (
                <Typography variant="body2" color="text.secondary" align="center">
                  After signing in, you'll be redirected to join the club.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  );
};

export default SignIn;
